import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Save } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import CatalogSelector from "@/components/CatalogSelector";

function isValidEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

export default function FanProfile() {
    const { fan, refresh } = useFan();
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState({
        name: "",
        email: "",
        city: "",
        country: "",
        image_url: "",
        preferred_genres: [],
        preferred_languages: [],
    });

    useEffect(() => {
        if (fan) {
            setForm({
                name: fan.name || "",
                email: fan.email || "",
                city: fan.city || "",
                country: fan.country || "",
                image_url: fan.image_url || "",
                preferred_genres: fan.preferred_genres || [],
                preferred_languages: fan.preferred_languages || [],
            });
        }
    }, [fan]);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((prev) => {
            if (!prev[k]) return prev;
            const { [k]: _, ...rest } = prev;
            return rest;
        });
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Display name is required.";
        if (!form.email.trim()) errs.email = "Email is required.";
        else if (!isValidEmail(form.email)) errs.email = "Enter a valid email.";
        return errs;
    };

    const submit = async () => {
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            toast.error("Please fix the highlighted fields");
            return;
        }
        setSaving(true);
        try {
            await endpoints.fanUpdate(form);
            toast.success("Profile saved");
            await refresh();
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!fan) return <div className="p-6 md:p-10 font-mono text-sm text-[#666]" data-testid="fan-profile-loading">Loading…</div>;

    return (
        <div className="p-4 md:p-6" data-testid="fan-profile-page">
            <div className="max-w-2xl space-y-6">
                <FormField label="Display name" error={errors.name}>
                    <input
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className={`w-full bg-transparent border-b py-2 outline-none text-lg ${errors.name ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                        data-testid="fan-profile-name"
                        aria-invalid={!!errors.name}
                    />
                </FormField>

                <FormField label="Email" error={errors.email}>
                    <input
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className={`w-full bg-transparent border-b py-2 outline-none ${errors.email ? "border-[#ff3333]" : "border-[#2a2a2a] focus:border-[#ff4400]"}`}
                        data-testid="fan-profile-email"
                        aria-invalid={!!errors.email}
                    />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="City">
                        <input
                            value={form.city}
                            onChange={(e) => set("city", e.target.value)}
                            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none"
                            data-testid="fan-profile-city"
                        />
                    </FormField>
                    <FormField label="Country">
                        <input
                            value={form.country}
                            onChange={(e) => set("country", e.target.value)}
                            className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none"
                            data-testid="fan-profile-country"
                        />
                    </FormField>
                </div>

                <FormField label="Profile image">
                    <ImageUploader
                        value={form.image_url}
                        onChange={(v) => set("image_url", v)}
                        scope="fan"
                        aspect="square"
                        testId="fan-profile-image"
                    />
                </FormField>

                <FormField label="Preferred genres">
                    <CatalogSelector
                        mode="multi"
                        value={form.preferred_genres}
                        onChange={(v) => set("preferred_genres", v)}
                        resource="genres"
                        placeholder="Search genres — e.g. Ethio-Jazz"
                        testId="fan-profile-genres"
                    />
                </FormField>

                <FormField label="Preferred languages">
                    <CatalogSelector
                        mode="multi"
                        value={form.preferred_languages}
                        onChange={(v) => set("preferred_languages", v)}
                        resource="languages"
                        placeholder="Search languages — e.g. Amharic"
                        testId="fan-profile-languages"
                    />
                </FormField>

                <div className="border border-[#2a2a2a] px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">
                    Only fields shown here are stored. Project Mint does not collect additional personal information.
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="pm-btn pm-btn-primary"
                        data-testid="fan-profile-save"
                    >
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save profile"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function FormField({ label, error, children }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] mb-1">{label}</div>
            {children}
            {error && <div className="mt-1 text-xs font-mono text-[#ff3333]">{error}</div>}
        </div>
    );
}
